
import React, { createContext, useContext, useReducer } from "react";
import { Movie } from "@/types/movie";

// Define the state type
type MovieDialogState = {
  expandedId: string | null;
  expandedEntity: Movie | null;
  isLoading: boolean;
  loadError: string | null;
};

// Define the action types
type MovieDialogAction =
  | { type: "OPEN_BY_ID"; payload: string }
  | { type: "OPEN_BY_ENTITY"; payload: Movie }
  | { type: "SET_LOADING"; payload: boolean }
  | { type: "SET_ERROR"; payload: string | null }
  | { type: "CLOSE" };

// Initial state
const initialState: MovieDialogState = {
  expandedId: null,
  expandedEntity: null,
  isLoading: false,
  loadError: null,
};

// Create the context
const MovieDialogContext = createContext<
  | {
      state: MovieDialogState;
      dispatch: React.Dispatch<MovieDialogAction>;
      expandedMovie: Movie | null; // Computed property for convenience
    }
  | undefined
>(undefined);

// Reducer function
function movieDialogReducer(
  state: MovieDialogState,
  action: MovieDialogAction
): MovieDialogState {
  switch (action.type) {
    case "OPEN_BY_ID":
      return {
        ...state,
        expandedId: action.payload,
        expandedEntity: null,
        loadError: null,
      };
    case "OPEN_BY_ENTITY":
      return {
        ...state,
        expandedId: null,
        expandedEntity: action.payload,
        loadError: null,
      };
    case "SET_LOADING":
      return {
        ...state,
        isLoading: action.payload,
      };
    case "SET_ERROR":
      return {
        ...state,
        loadError: action.payload,
      };
    case "CLOSE":
      return {
        ...state,
        expandedId: null,
        expandedEntity: null,
        isLoading: false,
        loadError: null,
      };
    default:
      return state;
  }
}

// Provider component
export const MovieDialogProvider: React.FC<{ children: React.ReactNode }> = ({
  children,
}) => {
  const [state, dispatch] = useReducer(movieDialogReducer, initialState);

  // The expanded movie is either the direct entity or null
  const expandedMovie = state.expandedEntity || null;

  return (
    <MovieDialogContext.Provider value={{ state, dispatch, expandedMovie }}>
      {children}
    </MovieDialogContext.Provider>
  );
};

// Hook to use the context
export const useMovieDialogContext = () => {
  const context = useContext(MovieDialogContext);
  if (!context) {
    throw new Error("useMovieDialogContext must be used within a MovieDialogProvider");
  }
  return context;
};
